import {
  defaultReportFilterConfig,
  IAnalytics,
  IReport,
  IReportFilterConfig,
  REPORTTYPE_ADVERTISER_RULES,
  REPORTTYPE_BID_FLOOR_SETTINGS_REPORT,
  REPORTTYPE_BID_SAVER_TRUE_LIFT,
  REPORTTYPE_BUYERS_APS,
  REPORTTYPE_CREATIVE_REVIEW,
  REPORTTYPE_CREATIVE_VALIDATION,
  REPORTTYPE_ELEA_AI_ENRICHMENT_BILLING,
  REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS,
  REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS_SUPER,
  REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER,
  REPORTTYPE_HEADERBIDDER_BYAUCTIONS,
  REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE,
  REPORTTYPE_HEADERBIDDER_BYBUYERS,
  REPORTTYPE_HEADERBIDDER_CAMPAIGN,
  REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS,
  REPORTTYPE_HEADERBIDDER_DEALS,
  REPORTTYPE_HEADERBIDDER_DESTINATIONS,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL,
  REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES,
  REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS,
  REPORTTYPE_HEADERBIDDER_SSP,
  REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT,
  REPORTTYPE_IAS_DEVICE,
  REPORTTYPE_IAS_FIRST_PARTY,
  REPORTTYPE_IAS_PERFORMANCE,
  REPORTTYPE_IAS_THIRD_PARTY,
  REPORTTYPE_IAS_YOUTUBE,
  REPORTTYPE_POD_HOURLY,
  REPORTTYPE_SHARE_OF_VOICE,
  REPORTTYPE_STREAM,
  REPORTTYPE_STREAM_USER_ACTIVITY,
  REPORTTYPE_THIRD_PARTY_TRACKER_DOMAINS,
  REPORTTYPE_TTD,
} from '~/src/dash/api/console-api';
import { IIASFirstAndThirdPartyRequestBody } from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import {
  DestinationType,
  DestinationTypeRoute,
  DestinationTypeSite,
  DestinationTypeUnknown,
  DestinationTypeVastURL,
  RoutingConditionPercentageRequests,
  RoutingConditionTargetingParams,
} from '~/src/dash/api/inventory_routes';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  auctionGroupByListExtendedOnly,
  auctionGroupByListFastOnly,
  auctionGroupByListWithAdomainOnly,
  auctionGroupByListWithDemandPathOnly,
  buyerGroupByListExtendedOnly,
  buyerGroupByListFastOnly,
  buyerGroupByListWithAdomainOnly,
  buyerGroupByListWithDemandPathOnly,
  IColumn,
} from '~/src/dash/utils/report-fields';

// https://github.com/publica-project/platform/blob/master/src/ads/logger/logger/bid_errortypes.go#L7
const BID_ERROR_CODE_MAP: Record<string, string> = {
  '': 'No Error',
  '0': 'Undefined (Deprecated)', // BidErrorCode.Undefined
  '1': 'Timeout Error', // BidErrorCode.TimeoutError
  '2': 'Bad Input Error', // BidErrorCode.BadInputError
  '3': 'Bad Server Response Error', // BidErrorCode.BadServerResponseError
  '4': 'Failed To Request Bids Error', // BidErrorCode.FailedToRequestBidsError
  '5': 'Bid Auction Failed', // BidErrorCode.BidAuctionFailed
  '6': 'Rate Limit Blocked', // BidErrorCode.RateLimitBlocked
  '7': 'Failed To Parse Response', // BidErrorCode.FailedToParseResponse
  '8': 'Connection Error', // BidErrorCode.ConnectionError
  '9': 'Panic', // BidErrorCode.Panic
  '10': 'Prebid Error', // BidErrorCode.PrebidError
  '11': 'Empty Response Error', // BidErrorCode.EmptyResponseError
  '12': 'No Content 204 Error', // BidErrorCode.NoContent204Error
  '13': 'No Fill Error', // BidErrorCode.NoFillError
  '14': 'Empty Vast Error', // BidErrorCode.EmptyVastError
  '15': 'Vast Validate Error', // BidErrorCode.VastValidateError
  '16': 'Cache Put Cache Miss', // BidErrorCode.CachePutCacheMiss
  '17': 'Unwrap Timeout Error', // BidErrorCode.UnwrapTimeoutError
  '18': 'Bid Price Below Floor', // BidErrorCode.BidPriceBelowFloor
  '19': 'Unknown Error', // BidErrorCode.UnknownError
  '20': 'Bid Rejected Unknown', // BidErrorCode.BidRejectedUnknown
  '21': 'Bid Rejected Duration', // BidErrorCode.BidRejectedDuration
  '22': 'Bid Rejected Dup Mediafile URL', // BidErrorCode.BidRejectedDupMediafileURL
  '23': 'Bid Rejected Dup Adomain', // BidErrorCode.BidRejectedDupAdomain
  '24': 'Bid Rejected Dup Category', // BidErrorCode.BidRejectedDupCategory
  '25': 'Bid Rejected Cache ID', // BidErrorCode.BidRejectedCacheID
  '26': 'Bid Rejected Price', // BidErrorCode.BidRejectedPrice
  '27': 'Bid Rejected Advertiser Blocklist', // BidErrorCode.BidRejectedAdvertiserBlocklist
  '28': 'Bid Rejected IAB Cat Blocklist', // BidErrorCode.BidRejectedIABCatBlocklist
  '29': 'Bid Rejected Adomain Missing', // BidErrorCode.BidRejectedAdomainMissing
  '30': 'Bid Rejected Above Max Ad Duration', // BidErrorCode.BidRejectedAboveMaxAdDuration
  '31': 'Bid Rejected Below Min Ad Duration', // BidErrorCode.BidRejectedBelowMinAdDuration
  '32': 'No Mediafile Satisfy Max Bitrate', // BidErrorCode.NoMediafileSatisfySiteMaxBitrate
  '33': 'Bid Rejected Pod Floor', // BidErrorCode.BidRejectedPodFloor
  '34': 'Bid Rejected All House Ads', // BidErrorCode.BidRejectedAllHouseAds
  '35': 'Bid Rejected Dup AdID', // BidErrorCode.BidRejectedDupAdID
  '36': 'Bid Rejected Dup CreativeID', // BidErrorCode.BidRejectedDupCreativeID
  '37': 'No Mediafile Satisfy Endpoint Max Bitrate', // BidErrorCode.NoMediafileSatisfyEndpointMaxBitrate
  '38': 'No Mediafile Satisfy Endpoint Min Bitrate', // BidErrorCode.NoMediafileSatisfyEndpointMinBitrate
  '39': 'No Mediafile Satisfy Site Min Bitrate', // BidErrorCode.NoMediafileSatisfySiteMinBitrate
  '40': 'Bid Rejected Brand Safety Rule Block', // BidErrorCode.BidRejectedBrandSafetyRuleBlock
  '41': 'Bid Rejected Min Pod Size', // BidErrorCode.MinPodSize
  '42': 'Bid Rejected Duplicate Bid Cat Exclusion', // BidErrorCode.BidRejectedDupBidCatExclusion
  '43': 'Bid Rejected Too Many Bid Cat Exclusions', // BidErrorCode.BidRejectedTooManyBidCatExclusion
  '44': 'Bid Price Missing', // BidErrorCode.BidPriceMissing
  '45': 'Bidder Inventory Type Not Shared', // BidErrorCode.BidderNotSharedInventory
  '46': 'Bid Rejected Dup Adomain (Elea)', // BidErrorCode.BidRejectedDupAdomainElea
  '47': 'Bid Rejected Dup Adomain (Clickthrough)', // BidErrorCode.BidRejectedDupAdomainClickThrough
  '48': 'Bid Rejected Dup Category (Elea)', // BidErrorCode.BidRejectedDupCategoryElea
  '49': 'Bid Rejected Price Above Error Threshold', // BidErrorCode.BidRejectedPriceAboveErrorThreshold
  '50': 'Bid Rejected Duplicate Campaign ID', // BidErrorCode.BidRejectedDupCampaignID
  '51': 'Bid Rejected Duplicate APS Key', // BidErrorCode.BidRejectedDupAPSKey
  '52': 'Bid Rejected Mediafile Block List', // BidErrorCode.BidRejectedMediafileBlocklist
  '53': 'Bid Not First Slot In Pod', // BidErrorCode.BidRejectedNotFirstSlotInPod
  '54': 'Bid Rejected Dup Mediafile URL Normal', // BidErrorCode.BidRejectedDupMediafileURLNormal
  '55': 'Bid Rejected Creative ID Blocked', // BidErrorCode.BidRejectedCreativeIDBlock
  '56': 'Bid Rejected Nonsecure VAST', // BidErrorCode.BidRejectedNonsecureVAST
  '57': 'Bid Rejected Slot Targeting Mismatch', // BidErrorCode.BidRejectedSlotTargetingMismatch
  '58': 'Invalid Bid Request URL', // BidErrorCode.InvalidRequestURL
  '59': 'Creative Block Unknown', // BidErrorCode.BidRejectedCreativeBlockUnknown
  '60': 'Creative Block 404 Not Found', // BidErrorCode.BidRejectedCreativeBlock404NotFound
  '61': 'Creative Block Mismatch Duration', // BidErrorCode.BidRejectedCreativeBlockMismatchDuration
  '62': 'Creative Validation Rejected', // BidErrorCode.BidRejectedCreativeValidationRejected
  '63': 'Over Order Frequency Cap', // BidErrorCode.BidRejectedOverOrderFrequencyCap
  '64': 'Over Campaign Frequency Cap', // BidErrorCode.BidRejectedCampaignOrderFrequencyCap
  '65': 'Site Loop Error', // BidErrorCode.BidSiteLoopError
  '66': 'Bid Rejected Blocked By Advertiser', // BidErrorCode.BidRejectedBlockedByAdvertiser
  '67': 'Bid Rejected Shared Pod Duration', // BidErrorCode.BidRejectedAboveSOVTypePodDuration
  '68': 'Bid Rejected Tier', // BidErrorCode.BidRejectedTier
  '69': 'Bid Rejected Over Max Unwrap', // BidErrorCode.MaxUnwrapError
  '70': 'Bid Rejected Language Blocklist', // BidErrorCode.BidRejectedLanguageBlocklist
  '71': 'Bid Missing Vast Meta', // BidErrorCode.BidMissingVastMeta
  '72': 'Bid Rejected XML Syntax Error', // BidErrorCode.XMLSyntaxError
  '73': 'Bid Rejected Advertiser Rule DNA', // BidErrorCode.BidRejectedAdvertiserRuleDoNotAir
  '74': 'Bid Rejected Advertiser Rule CompSep Self', // BidErrorCode.BidRejectedAdvertiserRuleCompetitiveSeparationSelf
  '75': 'Bid Rejected Advertiser Rule CompSep Other', // BidErrorCode.BidRejectedAdvertiserRuleCompetitiveSeparationOther
  '76': 'Bid Rejected Dup Philo Audio Ident', // BidErrorCode.BidRejectedDupPhiloAudioIdent
  '77': 'Invalid Mediafile Delivery Type', // BidErrorCode.InvalidMediafileDeliveryType
  '78': 'Bid Rejected Category Exclusivity', // BidErrorCode.BidRejectedCategoryExclusivity
  '79': 'Unwrap EOF Error', // BidErrorCode.UnwrapEOFError
  '80': 'Cache Connection', // BidErrorCode.CacheConnection
  '81': 'Cache XML Encode', // BidErrorCode.CacheXMLEncode
  '82': 'Cache Store', // BidErrorCode.CacheStore
  '83': 'Bid Connection Error', // BidErrorCode.BidConnectionError
  '84': 'Bid Rejected Event Tracker Block list', // BidErrorCode.BidRejectedEventTrackerBlocklist
  '85': 'Bid Price Too High', // BidErrorCode.BidPriceTooHigh
  '86': 'Bid Rejected Creative Quality Assurance', // BidErrorCode.BidRejectedCreativeQualityAssurance
  '87': 'Bid Rejected Platform Connection Bidder Slot Targeting Mismatch', // BidErrorCode.BidRejectedPlatformConnectionBidderSlotTargetingMismatch
  '88': 'Bid Rejected Below Floor', // BidErrorCode.BidRejectedBelowFloor
  '89': 'Combine Creative Error', // BidErrorCode.CombineCreativeError
  '90': 'Response EOF Error', // BidErrorCode.ResponseEOFError
  '91': 'Bid Connection Reset By Peer', // BidErrorCode.BidConnectionResetByPeer
  '92': 'Gzip Read Error', // BidErrorCode.GzipReadError
  '93': 'Unwrap Parse Error', // BidErrorCode.UnwrapParseError
  '94': 'Macro replacement Error', // BidErrorCode.MacroReplacementErrorCode
  '95': 'Other Unwrap Error', // BidErrorCode.OtherUnwrapError
  '96': 'Bid Rejected Private Auction Deal ID Mismatch', // BidErrorCode.BidRejectedPrivateAuctionDealIDMismatch
};

// https://github.com/publica-project/platform/blob/master/src/ads/logger/logger/endpoint_errortypes.go#L7
const ENDPOINT_ERROR_CODE_MAP: Record<string, string> = {
  '': 'No Error',
  '0': 'Undefined (Deprecated)', // BidErrorCode.Undefined
  '1': 'App Bundle Error', // BidErrorCode.AppBundleError
  '2': 'Site ID Error', // BidErrorCode.SiteIDError
  '3': 'Request Cap Reached', // BidErrorCode.RequestCapError
  '4': 'Parse Params Error', // BidErrorCode.ParseParamError
  '5': 'Site Inactive Error', // BidErrorCode.SiteInactiveError
  '6': 'Missing Impression Object', // BidErrorCode.ImpObjError
  '7': 'Missing IP Error', // BidErrorCode.MissingIPError
  '8': 'Missing User Agent Error', // BidErrorCode.MissingUAError
  '9': 'Missing Device Error', // BidErrorCode.MissingDeviceObjError
  '10': 'Site Page or Domain Error', // BidErrorCode.SitePageOrDomainError
  '11': 'Missing Route', // BidErrorCode.MissingRouteError
  '12': 'Parse Route Param Error', // BidErrorCode.ParseRouteParamError
  '13': 'No Matching Destination', // BidErrorCode.NoMatchingDestinationError
  '14': 'Unsupported Route Request Type', // BidErrorCode.UnsupportedRouteRequestType
  '15': 'Route ID Error', // BidErrorCode.RouteIDError
  '16': 'Marketplace blocking', // BidErrorCode.Marketplace
  '17': 'Max Depth Error', // BidErrorCode.MaxDepthError
  '18': 'Inner Auction Raw Panic', // BidErrorCode.InnerAuctionRawPanic
  '19': 'Inventory Route Fw Req Panic', // BidErrorCode.InventoryRouteFwReqPanic
  '20': 'Unknown Panic', // BidErrorCode.UnknownPanic
  '21': 'App Bundle Is Blocked Error', // BidErrorCode.AppBundleIsBlockedError
  '22': 'Missing Destination Site ID', // BidErrorCode.MissingDestinationSiteID
  '23': 'Invalid Domain', // BidErrorCode.ResellerInvalidDomain
};

// https://github.com/publica-project/platform/blob/03d826ce13770bf2ff9c26da380b521dc8662a49/src/proto/logger/vast_error_codes.go#L1-L148
const VAST_ERROR_CODE_MAP: Record<string, string> = {
  '': 'No Error',
  '0': 'GenericVastError',
  '1': 'XMLParsingError',
  '2': 'VastSchemaValidationError',
  '3': 'VastResponseVersionError',
  '4': 'TraffickingError',
  '5': 'MediaPlayerLinearityError',
  '6': 'MediaPlayerDurationError',
  '7': 'MediaPlayerSizeError',
  '8': 'AdCategoryReqError',
  '9': 'InlineCategoryError',
  '10': 'AdBreakError',
  '11': 'GeneralWrapperError',
  '12': 'WrapperVastUriError',
  '13': 'TimeoutVastError',
  '14': 'WrapperLimitError',
  '15': 'NoVastResponseError',
  '16': 'InlineResponseAdUnitError',
  '17': 'GeneralLinearError',
  '18': 'FileNotFoundError',
  '19': 'TimeoutMediaFileError',
  '20': 'MediaFileNotSupportedError',
  '21': 'MediaFileDisplayError',
  '22': 'MezzanineReqError',
  '23': 'MezzanineDownloadError',
  '24': 'ConditionalAdError',
  '25': 'InteractiveUnitError',
  '26': 'VerificationUnitError',
  '27': 'MezzanineSpecError',
  '28': 'GeneralNonLinearError',
  '29': 'NonLinearCreativeDimensionsError',
  '30': 'UnableFetchNonLinearError',
  '31': 'UnableFindNonLinearError',
  '32': 'GeneralCompanionAdsError',
  '33': 'CompanionCreativeDimensionsError',
  '34': 'DisplayCompanionError',
  '35': 'UnableFetchCompanionError',
  '36': 'UnableFindCompanionError',
  '37': 'UndefinedError',
  '38': 'GeneralVPAIDError',
  '39': 'GeneralInteractiveCreativeError',
  '40': 'LanguageBlockListError',
  '41': 'MissingVastMetaError',
  '42': 'CreativeReviewBlockedStatusError',
  '43': 'AdvertiserRuleDoNotAirError',
  '44': 'AdvertiserRuleCompetitiveSeparationSelfError',
  '45': 'AdvertiserRuleCompetitiveSeparationOtherError',
  '46': 'DupPhiloAudioIdentError',
  '47': 'CategoryExclusivityError',
  '48': 'CacheConnectionError',
  '49': 'CacheXMLEncodeError',
  '50': 'CacheStoreError',
  '51': 'EventTrackerBlockListError',
  '52': 'PrivateAuctionDealIDMismatchError',
};

const CAMPAIGN_NO_FILL_ERROR_CODE_MAP: Record<string, string> = {
  '': 'No error',
  '0': 'No error',
  '1': 'Empty Vast',
  '2': 'Frequency Cap',
  '3': 'Pacing',
  '4': 'Site loop error',
  '5': 'Empty vast mediafile',
  '6': 'Creative selection error',
  '7': 'Mediafile has zero duration',
  '8': 'Adserver Order not found in cache',
  '9': 'Low Pacing',
  '10': 'Order Frequency Cap',
  '11': 'Vast Timeout',
  '12': 'EMA Pacing Diceroll Rejection',
  '13': 'EMA Pacing Data not found',
};

// https://github.com/publica-project/platform/blob/master/src/ads/logger/logger/bid_reject_reasons.go#L7
const BID_WON_REJECT_REASON_MAP: Record<string, string> = {
  '': '-',
  '0': 'Unknown',
  '1': 'Duration',
  '2': 'DupMediaFileURL',
  '3': 'DupAdomain',
  '4': 'DupCategory',
  '5': 'CacheID',
  '6': 'Price',
  '7': 'AdvertiserBlocklist',
  '8': 'IABCatBlocklist',
  '9': 'AdomainMissing',
  '10': 'AboveMaxAdDuration',
  '11': 'BelowMinAdDuration',
  '12': 'PodFloor',
  '13': 'AllHouseAds',
  '14': 'DupAdID',
  '15': 'DupCreativeID',
  '16': 'BrandSafetyRuleBlock',
  '17': 'MinPodSizeNotMeet',
  '18': 'CombineCreative',
  '19': 'DupBidCatExclusion',
  '20': 'TooManyBidCatExclusion',
  '21': 'NotSharedInventory',
  '22': 'DupAdomainElea',
  '23': 'DupAdomainClickThrough',
  '24': 'DupCategoryElea',
  '25': 'PriceAboveErrorThreshold',
  '26': 'DupCampaignID',
  '27': 'DupAPSKey',
  '28': 'MediafileBlocklist',
  '29': 'NotFirstSlotInPod',
  '30': 'DupMediafileURLNormal',
  '31': 'CreativeIDBlocklist',
  '32': 'NonsecureVAST',
  '33': 'SlotTargetingMismatch',
  '34': 'OverOrderFrequencyCap',
  '35': 'OverCampaignFrequencyCap',
  '36': 'SiteLoopError',
  '37': 'BlockedByAdvertiser',
  '38': 'AboveSOVTypePodDuration',
  '39': 'Tier',
  '40': 'LanguageBlocklist',
  '41': 'MissingVastMeta',
  '42': 'CreativeReviewBlocked',
  '43': 'AdvertiserRuleDoNotAir',
  '44': 'AdvertiserRuleCompetitiveSeparationSelf',
  '45': 'AdvertiserRuleCompetitiveSeparationOther',
  '46': 'DupPhiloAudioIdent',
  '47': 'CategoryExclusivity',
  '48': 'CacheConnection',
  '49': 'CacheXMLEncode',
  '50': 'CacheStore',
  '51': 'EventTrackerBlockList',
  '52': 'PrivateAuctionDealIDMismatch',
  '53': 'DurationMismatchBetweenRequiredDuration',
};

const CREATIVE_REVIEW_STATUS_MAP: Record<string, string> = {
  '': '-',
  '0': 'Unreviewed',
  '1': 'Marked As Reviewed',
  '2': 'Blocked Creative',
  '3': 'Allowed Creative',
};

// UPDATE WHEN ADDING NEW DEVICE ENVIRONMENT
const TARGETING_DEVICE_MAP: Record<string, string> = {
  HEADERBIDDER_TARGETING_DEVICE_ALL: 'All',
  HEADERBIDDER_TARGETING_ANDROID_MOBILE: 'Android Mobile',
  HEADERBIDDER_TARGETING_ANDROID_TABLET: 'Android Tablet',
  HEADERBIDDER_TARGETING_ANDROID_TV: 'Android TV',
  HEADERBIDDER_TARGETING_APPLE_TV: 'Apple TV',
  HEADERBIDDER_TARGETING_BRAVIA: 'Bravia',
  HEADERBIDDER_TARGETING_CHROMECAST: 'Chromecast',
  HEADERBIDDER_TARGETING_COMCAST: 'Comcast',
  HEADERBIDDER_TARGETING_DISH: 'Dish',
  HEADERBIDDER_TARGETING_FIRE_TV: 'Fire TV',
  HEADERBIDDER_TARGETING_HISENSE: 'Hisense',
  HEADERBIDDER_TARGETING_HUAWEI: 'Huawei',
  HEADERBIDDER_TARGETING_IOS_MOBILE: 'iOS Mobile',
  HEADERBIDDER_TARGETING_IOS_TABLET: 'iOS Tablet',
  HEADERBIDDER_TARGETING_LG: 'LG',
  HEADERBIDDER_TARGETING_NVIDIA: 'Nvidia',
  HEADERBIDDER_TARGETING_PANASONIC: 'Panasonic',
  HEADERBIDDER_TARGETING_PHILLIPS: 'Phillips',
  HEADERBIDDER_TARGETING_PLAYSTATION: 'Playstation',
  HEADERBIDDER_TARGETING_ROKU: 'Roku',
  HEADERBIDDER_TARGETING_SAMSUNG_TV: 'Samsung TV',
  HEADERBIDDER_TARGETING_SANYO: 'Sanyo',
  HEADERBIDDER_TARGETING_SHARP: 'Sharp',
  HEADERBIDDER_TARGETING_TCL: 'Tcl',
  HEADERBIDDER_TARGETING_TIVO: 'Tivo',
  HEADERBIDDER_TARGETING_VIZIO: 'Vizio',
  HEADERBIDDER_TARGETING_XBOX: 'Xbox',
  HEADERBIDDER_TARGETING_OTHER: 'Other',
  HEADERBIDDER_TARGETING_DESKTOPWEB: 'Desktop',
  HEADERBIDDER_TARGETING_AIRTV: 'AirTV',
  HEADERBIDDER_TARGETING_XIAOMI: 'Xiaomi',
  HEADERBIDDER_TARGETING_ATT: 'AT&T',
  HEADERBIDDER_TARGETING_SAMSUNGFAMILYHUB: 'Samsung Family Hub',
};

// UPDATE WHEN ADDING NEW DEVICE ENVIRONMENT
export const deviceTargetingMap: Record<string, string> = {
  0: '',
  1: 'HEADERBIDDER_TARGETING_ANDROID_MOBILE',
  2: 'HEADERBIDDER_TARGETING_ANDROID_TABLET',
  3: 'HEADERBIDDER_TARGETING_ANDROID_TV',
  4: 'HEADERBIDDER_TARGETING_FIRE_TV',
  5: 'HEADERBIDDER_TARGETING_IOS_MOBILE',
  6: 'HEADERBIDDER_TARGETING_IOS_TABLET',
  7: 'HEADERBIDDER_TARGETING_APPLE_TV',
  8: 'HEADERBIDDER_TARGETING_PLAYSTATION',
  9: 'HEADERBIDDER_TARGETING_ROKU',
  10: 'HEADERBIDDER_TARGETING_XBOX',
  11: 'HEADERBIDDER_TARGETING_SAMSUNG_TV',
  12: 'HEADERBIDDER_TARGETING_CHROMECAST',
  13: 'HEADERBIDDER_TARGETING_OTHER',
  14: 'HEADERBIDDER_TARGETING_VIZIO',
  15: 'HEADERBIDDER_TARGETING_LG',
  16: 'HEADERBIDDER_TARGETING_DISH',
  17: 'HEADERBIDDER_TARGETING_HISENSE',
  18: 'HEADERBIDDER_TARGETING_NVIDIA',
  19: 'HEADERBIDDER_TARGETING_PHILLIPS',
  20: 'HEADERBIDDER_TARGETING_HUAWEI',
  21: 'HEADERBIDDER_TARGETING_TCL',
  22: 'HEADERBIDDER_TARGETING_TIVO',
  23: 'HEADERBIDDER_TARGETING_BRAVIA',
  24: 'HEADERBIDDER_TARGETING_COMCAST',
  25: 'HEADERBIDDER_TARGETING_SHARP',
  26: 'HEADERBIDDER_TARGETING_PANASONIC',
  27: 'HEADERBIDDER_TARGETING_SANYO',
  28: 'HEADERBIDDER_TARGETING_DESKTOPWEB',
  29: 'HEADERBIDDER_TARGETING_AIRTV',
  30: 'HEADERBIDDER_TARGETING_XIAOMI',
  31: 'HEADERBIDDER_TARGETING_ATT',
  32: 'HEADERBIDDER_TARGETING_SAMSUNGFAMILYHUB',
};

export function getReportGroupType(groupByOptions: string) {
  const containSitename = groupByOptions.indexOf('sitename') > -1;
  const containPosition = groupByOptions.indexOf('adunitposition') > -1;

  if (!containSitename && !containPosition) {
    return 0;
  } else if (containSitename && !containPosition) {
    return 1;
  } else if (!containSitename && containPosition) {
    return 2;
  }
  return 3;
}

// check the params and filter options for extended table group by options
function isExtendedGraphUsed(
  reportType: string,
  groupByOptions: string[],
  filterOptions?: string[],
): boolean {
  // Set of all buyers extended, extended fast, adomain, and demand path group bys
  const buyerExtendedGroupByParams = new Set(
    buyerGroupByListExtendedOnly.concat(
      buyerGroupByListFastOnly,
      buyerGroupByListWithAdomainOnly,
      buyerGroupByListWithDemandPathOnly,
    ),
  );

  // Clean group by options
  const unquotedGroupByOptions = groupByOptions.map((gb: string) => gb.replace(/'/g, ''));

  // Set of all auctions extended, extended fast, adomain, and demand path group bys
  const auctionExtendedGroupByParams = new Set(
    auctionGroupByListExtendedOnly.concat(
      auctionGroupByListFastOnly,
      auctionGroupByListWithAdomainOnly,
      auctionGroupByListWithDemandPathOnly,
    ),
  );

  // Check if report has extended, if it does choose which params we care about
  let params: Set<string>;
  if (reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS) {
    params = buyerExtendedGroupByParams;
  } else if (reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS) {
    params = auctionExtendedGroupByParams;
  } else {
    return false;
  }

  // Loop through the selected group by options and see if it is an extended group by
  for (const groupBy of unquotedGroupByOptions) {
    if (params.has(groupBy)) {
      return true;
    }
  }

  // Loop through the selected filter options and see if it is an extended filter
  if (filterOptions && filterOptions.length > 0) {
    for (const filterOption of filterOptions) {
      if (params.has(filterOption)) {
        return true;
      }
    }
  }

  return false;
}

/** *** GRAPH IDS *****/
const BUYERS_EXTENDED_GRAPH = '41';
const AUCTIONS_STANDARD_GRAPH = '27';
const AUCTIONS_EXTENDED_GRAPH = '40';

export function getReportGraphID(
  reportType: string,
  groupByOptions: string[],
  metrics?: string[],
): string {
  // HEADERBIDDERBYBUYERS
  // for auctions and buyers, first check if the materialized view can be used since it is faster, the table only goes back 6 months
  if (reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS) {
    return BUYERS_EXTENDED_GRAPH;
  }

  // HEADERBIDDERBYAUCTIONS
  if (reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS) {
    if (isExtendedGraphUsed(reportType, groupByOptions, metrics)) {
      return AUCTIONS_EXTENDED_GRAPH;
    }
    return AUCTIONS_STANDARD_GRAPH;
  }

  // SSP
  if (reportType === REPORTTYPE_HEADERBIDDER_SSP) {
    return '35';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_CAMPAIGN) {
    return '39';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS) {
    return '44';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS_SUPER) {
    return '55';
  }
  if (reportType === REPORTTYPE_STREAM) {
    return '47';
  }
  if (reportType === REPORTTYPE_STREAM_USER_ACTIVITY) {
    return '107';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT) {
    return '49';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE) {
    return '56';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER) {
    return '64';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_DEALS) {
    return '65';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS) {
    return '68';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL) {
    return '80';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT) {
    return '81';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES) {
    return '74';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_DESTINATIONS) {
    return '75';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS) {
    return '76';
  }
  if (reportType === REPORTTYPE_IAS_THIRD_PARTY) {
    return '3';
  }
  if (reportType === REPORTTYPE_IAS_FIRST_PARTY) {
    return '4';
  }
  if (reportType === REPORTTYPE_CREATIVE_VALIDATION) {
    return '78';
  }
  if (reportType === REPORTTYPE_IAS_PERFORMANCE) {
    return '19';
  }
  if (reportType === REPORTTYPE_IAS_DEVICE) {
    return '24';
  }
  if (reportType === REPORTTYPE_IAS_YOUTUBE) {
    return '25';
  }
  if (reportType === REPORTTYPE_TTD) {
    return '82';
  }
  if (reportType === REPORTTYPE_CREATIVE_REVIEW) {
    return '85';
  }
  if (reportType === REPORTTYPE_SHARE_OF_VOICE) {
    return '90';
  }
  if (reportType === REPORTTYPE_POD_HOURLY) {
    return '91';
  }
  if (reportType === REPORTTYPE_ADVERTISER_RULES) {
    return '92';
  }
  if (reportType === REPORTTYPE_BUYERS_APS) {
    return '93';
  }
  if (reportType === REPORTTYPE_THIRD_PARTY_TRACKER_DOMAINS) {
    return '94';
  }
  if (reportType === REPORTTYPE_ELEA_AI_ENRICHMENT_BILLING) {
    return '95';
  }
  if (reportType === REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION) {
    return '96';
  }
  if (reportType === REPORTTYPE_BID_FLOOR_SETTINGS_REPORT) {
    return '98';
  }
  if (reportType === REPORTTYPE_BID_SAVER_TRUE_LIFT) {
    return '101';
  }
  // ADBLOCK
  return '26';
}

export const keyToSQLField: { [key: string]: string } = {
  AbTestingConfigName: 'ab_testing_config_name',
  AbTestingTestName: 'ab_testing_test_name',
  ActiveBidderTier: 'active_bidder_tier',
  ActiveMargin: 'active_margin',
  AdBreakPosition: 'ad_break_position',
  AdDuration: 'ad_duration',
  AdRenderFaileds: 'ad_render_faileds',
  Adomain: 'adomain',
  AdSourceID: 'ad_source_id',
  APKBuild: 'apk_build',
  AppBundleID: 'app_bundle_id',
  AppliedContextualSegmentIDs: 'applied_contextual_segment_ids',
  AppliedContextualSegmentNames: 'applied_contextual_segment_names',
  AppName: 'app_name',
  APSBidsSubmitted: 'aps_bids_submitted',
  APSKey: 'aps_key',
  AuctionEnds: 'auction_ends',
  AuctionLatency: 'auction_latency',
  AuctionClearRate: 'auction_clear_rate',
  AudienceID: 'audience_id',
  AvgBid: 'avg_bid',
  AvgDuration: 'avg_duration',
  AvgImpECPM: 'avg_imp_ecpm',
  AvgImpGrossECPM: 'avg_imp_gross_ecpm',
  AvgWinningBid: 'avg_winning_bid',
  BidAuctions: 'bid_auctions',
  BidDealId: 'bid_deal_id',
  OutboundDealId: 'outbound_deal_id',
  BidDensity: 'bid_density',
  BidDones: 'bid_dones',
  BidErrorCode: 'bid_error_code',
  BidErrors: 'bid_errors',
  BidErrorRate: 'bid_error_rate',
  BidLosts: 'bid_losts',
  BidLossRatePrice: 'bid_loss_rate_price',
  BidLossRateTier: 'bid_loss_rate_tier',
  BidNone: 'bid_none',
  BidRequests: 'bid_requests',
  BidRequestsRejected: 'bid_requests_rejected',
  BidRequestRate: 'bid_request_rate',
  BidResponseDealID: 'bid_response_deal_id',
  BidResponseRate: 'bid_response_rate',
  BidResponses: 'bid_responses',
  BidResponsesRejected: 'bid_responses_rejected',
  BidSaverTrueLift: 'bid_saver_true_lift',
  BidTimeouts: 'bid_timeouts',
  BidTimeoutsRate: 'bid_timeouts_rate',
  BidWonRejectReason: 'bid_won_reject_reason',
  BidWons: 'bid_wons',
  BidderGroup: 'bidder_group',
  BidderID: 'bidder_id',
  BidderLabel: 'bidder_label',
  BidderName: 'biddername',
  BidderTesting: 'bidder_testing',
  BidderTier: 'bidder_tier',
  BidderSubTier: 'bidder_subtier',
  BidderType: 'bidder_type',
  BrandSafetyRuleID: 'brand_safety_rule_id',
  BrandSafetyTrackerName: 'brand_safety_tracker_name',
  BrandSafetyTrackerType: 'brand_safety_tracker_type',
  BuyersLatency: 'buyers_latency',
  ClearRateByPodDuration: 'clear_rate_by_pod_duration',
  CPM: 'cpm',
  CPMPerSecond: 'cpm_per_second',
  CampaignComplete: 'campaign_complete',
  CampaignFilled: 'campaign_filled',
  CampaignImpression: 'campaign_impression',
  CampaignName: 'campaignname',
  CampaignQuartile1: 'campaign_quartile1',
  CampaignQuartile2: 'campaign_quartile2',
  CampaignQuartile3: 'campaign_quartile3',
  CampaignRequest: 'campaign_request',
  CampaignInventoryRequest: 'campaign_inventory_request',
  CampaignStart: 'campaign_start',
  Category: 'category',
  ChannelGroup: 'channel_group',
  ChannelLabel: 'channel_label',
  City: 'city',
  Coppa: 'coppa',
  CostToPublisher: 'cost_to_publisher',
  Country: 'country',
  CountryGroup: 'country_group',
  Complete: 'complete',
  CompletionRate: 'completion_rate',
  CreativeID: 'creative_id',
  CreativeName: 'creative_name',
  CreativeErrors: 'creative_errors',
  CreativeTagRequests: 'creative_tag_requests',
  CreativeTagResponses: 'creative_tag_responses',
  Custom10: 'custom10',
  Custom11: 'custom11',
  Custom12: 'custom12',
  Custom13: 'custom13',
  Custom14: 'custom14',
  Custom15: 'custom15',
  Custom16: 'custom16',
  Custom17: 'custom17',
  Custom18: 'custom18',
  Custom1: 'custom1',
  Custom2: 'custom2',
  Custom3: 'custom3',
  Custom4: 'custom4',
  Custom5: 'custom5',
  Custom6: 'custom6',
  Custom7: 'custom7',
  Custom8: 'custom8',
  Custom9: 'custom9',
  CustomTargeting: 'custom_targeting',
  DMA: 'dma',
  DataProvider: 'data_provider',
  DataType: 'data_type',
  Date: 'date',
  Day: 'day',
  DealName: 'deal_name',
  DedupBypass: 'dedup_bypass',
  DeliveredPodDuration: 'delivered_pod_duration',
  DemandPath: 'demand_path',
  DemandSidePlatformID: 'demand_side_platform_id',
  DestinationErrorCode: 'destination_error_code',
  DestinationID: 'destination_id',
  DestinationImpressions: 'destination_impressions',
  DestinationName: 'destination_name',
  DestinationSiteID: 'destination_site_id',
  DestinationSiteName: 'destination_site_name',
  DestinationType: 'destination_type',
  DestinationRequests: 'destination_requests',
  DestinationResponses: 'destination_responses',
  DestinationTimeouts: 'destination_timeouts',
  DestinationRequestErrors: 'destination_request_errors',
  DestinationNoFills: 'destination_no_fills',
  DestinationRequestLatency: 'destination_request_latency',
  DeviceGeoRegion: 'device_geo_region',
  DeviceType: 'devicetype',
  DeviceSubType: 'device_subtype',
  DeviceEnv: 'deviceenv',
  DoNotTrack: 'do_not_track',
  DspName: 'dsp_name',
  EndpointErrorCode: 'endpoint_error_code',
  EndpointLatency: 'endpoint_latency',
  EndpointRequests: 'endpoint_requests',
  EndpointRequestECPM: 'endpoint_request_ecpm',
  EndpointResponses: 'endpoint_responses',
  EventEndpointErrors: 'event_endpoint_errors',
  FlightDateCheckpointID: 'flight_date_checkpoint_id',
  FlightDateCheckpointName: 'flight_date_checkpoint_name',
  FilledAds: 'filled_ads',
  FilledPodDuration: 'filled_pod_duration',
  FilledVMAPDuration: 'filled_vmap_duration',
  FillRate: 'fillrate',
  FillRateByDemandSource: 'fill_rate_by_demand_source',
  FwRenderRate: 'fw_render_rate',
  FwSectionName: 'fw_section_name',
  FwSectionIdV2: 'fw_section_id_v2',
  FwWinRate: 'fw_win_rate',
  DeliveredAds: 'delivered_ads',
  Gdpr: 'gdpr',
  GdprConsent: 'gdpr_consent',
  GlobalMediafileID: 'global_mediafile_id',
  GlobalVastID: 'global_vast_id',
  GrossRevenue: 'gross_revenue',
  Hour: 'hour',
  ImpressedBidderType: 'impressed_bidder_type',
  Impressions: 'impressions',
  InboundDealId: 'inbound_deal_id',
  InboundWseat: 'inbound_wseat',
  InventoryRouteID: 'inventory_route_id',
  InventoryRouteName: 'inventory_route_name',
  InventoryRouteCondition: 'inventory_route_condition',
  InventoryRouteErrorCode: 'inventory_route_error_code',
  InventoryRouteRequests: 'inventory_route_requests',
  InventoryRouteResponses: 'inventory_route_responses',
  InventoryRouteErrors: 'inventory_route_errors',
  InventoryRouteNoFills: 'inventory_route_no_fills',
  InventoryRouteEndpointLatency: 'inventory_route_endpoint_latency',
  InventoryRouteResponseDestinationID: 'inventory_route_response_destination_id',
  InventoryType: 'inventory_type',
  IPType: 'ip_type',
  IASContextualInfo: 'ias_contextual_info',
  IrisContext: 'iris_context',
  IRISContextualInfo: 'iris_contextual_info',
  IrisID: 'iris_id',
  IsBidSaver: 'is_bid_saver',
  IsFallbackResponse: 'is_fallback_response',
  IsMultiBid: 'is_multi_bid',
  IsPoddedRequest: 'is_podded_request',
  LiverampCPM: 'liveramp_cpm',
  LiverampRevenue: 'liveramp_revenue',
  LiverampSegmentIDs: 'liveramp_segment_ids',
  Livestream: 'livestream',
  LRDmProviders: 'lr_dm_providers',
  LRDmSegIDs: 'lr_dm_seg_ids',
  LRDmDataFee: 'lr_dm_data_fee',
  LR1pMatchingFee: 'lr_1p_matching_fee',
  LR3pMatchingFee: 'lr_3p_matching_fee',
  MaxAvailablePodDurationByTier: 'max_available_pod_duration_by_tier',
  Month: 'month',
  NativeCompanionClick: 'native_companion_click',
  NativeCreativeView: 'native_creative_view',
  NativeVideoClick: 'native_video_click',
  NetUnitCost: 'net_unit_cost',
  NumberOfAdsRequested: 'number_of_ads_requested',
  OrderBuyType: 'order_buy_type',
  ORTBSiteName: 'ortb_site_name',
  PartnerCPM: 'partner_cpm',
  PartnerRevenue: 'partner_revenue',
  PaidFilledPodDuration: 'paid_filled_pod_duration',
  PaidPodClearRate: 'paid_pod_clear_rate',
  PodClearRate: 'pod_clear_rate',
  PodDuration: 'pod_duration',
  RenderedPodFillRate: 'rendered_pod_fill_rate',
  PodFillRate: 'pod_fill_rate',
  PublicaCPM: 'publica_cpm',
  PublicaRevenue: 'publica_revenue',
  PublisherCPM: 'publisher_cpm',
  PublisherMargin: 'publisher_margin',
  PublisherName: 'publisher_name',
  RecognitionResultEnrichedLanguageInput: 'recognition_result_enriched_language_input',
  RenderRate: 'render_rate',
  RenderRateByPodDuration: 'render_rate_by_pod_duration',
  RenderedRequestedPodDuration: 'rendered_requested_pod_duration',
  RequestedPodDuration: 'requested_pod_duration',
  RequestedVMAPDuration: 'requested_vmap_duration',
  Revenue: 'revenue',
  RokuAdKeys: 'roku_ad_keys',
  SamsungModel: 'samsung_model',
  SegmentIDs: 'segmentids',
  SegmentName: 'segmentname',
  SharedFilledVMAPDuration: 'shared_requested_vmap_duration',
  SharedRequestedVMAPDuration: 'shared_filled_vmap_duration',
  ShareOfVoice: 'share_of_voice',
  ShareOfVoiceType: 'share_of_voice_type',
  ShareType: 'share_type',
  SiteID: 'siteid',
  SiteName: 'sitename',
  SitePage: 'site_page',
  SOVInventoryType: 'sov_inventory_type',
  SOVParallelAuctionsEnabled: 'sov_parallel_auctions_enabled',
  SOVParallelAuctionsBackfillEnabled: 'sov_parallel_auctions_backfill_enabled',
  SpotxEndpoint: 'spotx_endpoint',
  SpotxParamChannelID: 'spotx_param_channel_id',
  State: 'state',
  EventStart: 'event_start',
  TotalRenderedRequestedPodDuration: 'total_rendered_requested_pod_duration',
  TotalRequestedPodDuration: 'total_requested_pod_duration',
  TotalFilledPodDuration: 'total_filled_pod_duration',
  TotalPaidFilledPodDuration: 'total_paid_filled_pod_duration',
  TotalDeliveredPodDuration: 'total_delivered_pod_duration',
  TargetingDevice: 'targeting_device',
  UsPrivacy: 'us_privacy',
  VastErrorCode: 'vast_error_code',
  VastErrors: 'vast_errors',
  VenueID: 'venue_id',
  WeekOfYear: 'week_of_year',
  WinRate: 'win_rate',
  Zip: 'zip',

  // SSAI
  AdComplete: 'ad_complete',
  AdFilled: 'ad_filled',
  AdImpression: 'ad_impression',
  AdImpressionSS: 'ad_impression_ss',
  AdFirstQuartile: 'ad_first_quartile',
  AdMidPoint: 'ad_mid_point',
  AdThirdQuartile: 'ad_third_quartile',
  AvgStreamAdFilledLatency: 'avg_stream_ad_filled_latency',
  AvgStreamChunklistResponseLatency: 'avg_stream_chunklist_response_latency',
  AvgStreamResponseLatency: 'avg_stream_response_latency',
  AvgStreamStartPerSession: 'avg_stream_start_per_session',
  AvgPodRequestPerSession: 'avg_pod_request_per_session',
  AvgPodFilledPerSession: 'avg_pod_filled_per_session',
  AvgPodCompletePerSession: 'avg_pod_complete_per_session',
  AvgAdFilledPerSession: 'avg_ad_filled_per_session',
  AvgAdImpressionPerSession: 'avg_ad_impression_per_session',
  AvgAdFirstQuartilePerSession: 'avg_ad_first_quartile_per_session',
  AvgAdMidPointPerSession: 'avg_ad_mid_point_per_session',
  AvgAdThirdQuartilePerSession: 'avg_ad_third_quartile_per_session',
  AvgAdCompletePerSession: 'avg_ad_complete_per_session',
  AvgStreamErrorPerSession: 'avg_stream_error_per_session',
  CstAdFilled: 'cst_ad_filled',
  CstAdImpression: 'cst_ad_impression',
  CstRenderRate: 'cst_render_rate',
  HLSReject: 'hls_reject',
  IsPartialAdBreak: 'is_partial_ad_break',
  NetworkID: 'network_id',
  NetworkName: 'network_name',
  NetworkChannelID: 'network_channel_id',
  NetworkChannelName: 'network_channel_name',
  NetworkChannelSiteID: 'network_channel_site_id',
  NetworkChannelSiteName: 'network_channel_site_name',
  NonCstAdFilled: 'non_cst_ad_filled',
  NonCstAdImpression: 'non_cst_ad_impression',
  NonCstRenderRate: 'non_cst_render_rate',
  PodStart: 'pod_start',
  PodStartSS: 'pod_start_ss',
  PodComplete: 'pod_complete',
  PodCompleteRate: 'pod_complete_rate',
  PodFilled: 'pod_filled',
  PodRequest: 'pod_request',
  SlotNumber: 'slot_number',
  StreamAdsFetchTimeout: 'stream_ads_fetch_timeout',
  StreamChunklistTimeout: 'stream_chunklist_timeout',
  StreamError: 'stream_error',
  StreamErrorCode: 'stream_error_code',
  StreamFormatType: 'stream_format_type',
  StreamPlaylistTimeout: 'stream_playlist_timeout',
  StreamStart: 'stream_start',
  DeviceMake: 'device_make',
  DeviceModel: 'device_model',
  DeviceOS: 'device_os',
  DurationExceeded: 'duration_exceeded',
  // SSAI User Activity Report
  UniqueSessions: 'uniquesessions',
  UniqueViewers: 'unique_viewers',
  SessionsPerUser: 'sessions_per_user',
  AvgWatchTimePerSession: 'avg_watch_time_per_session',
  AvgWatchTimePerViewer: 'avg_watch_time_per_viewer',
  NumOfUsers: 'num_of_users',

  // Unique User Report
  ContentID: 'content_id',
  DealID: 'deal_id',
  SyntheticID: 'synthetic_id',
  PresetAggregateForYesterday: 'preset_aggregate_yesterday',
  PresetAggregateForLastSevenDays: 'preset_aggregate_last_seven_days',
  PresetAggregateForLastThirtyDays: 'preset_aggregate_last_thirty_days',
  UniqueUsers: 'unique_users',

  // Campaign
  CampaignNoFill: 'campaign_no_fill',
  NoFillReason: 'no_fill_reason',
  StartDate: 'start_date',
  LineItemCount: 'line_item_count',

  // Extended Line Items Associations
  LineItemAlias: 'line_item_alias',
  OrderAlias: 'order_alias',

  // Line Item Map
  AgencyName: 'agency_name',
  AdvertiserName: 'advertiser_name',
  LineItemStatus: 'line_item_status',
  LineItemID: 'line_item_id',
  OrderName: 'order_name',
  NeedsCreative: 'needs_creative',
  SalespersonName: 'salesperson_name',
  TraffickerName: 'trafficker_name',

  // VMAP - Philo only
  VMAPRequests: 'vmap_requests',
  VMAPResponses: 'vmap_responses',
  VMAPLatency: 'vmap_latency',
  // Fallback - Philo only
  PhiloFallback: 'philo_fallback',

  // Content
  ContentCat: 'content_cat',
  ContentChannel: 'content_channel',
  ContentContext: 'content_context',
  ContentDistID: 'content_dist_id',
  ContentDistName: 'content_dist_name',
  ContentEpisode: 'content_episode',
  ContentGenre: 'content_genre',
  ContentKeywords: 'content_keywords',
  ContentLanguage: 'content_language',
  ContentLength: 'content_length',
  ContentNetwork: 'content_network',
  ContentProdQual: 'content_prodqual',
  ContentProducerID: 'content_producer_id',
  ContentProducerName: 'content_producer_name',
  ContentRating: 'content_rating',
  ContentSeason: 'content_season',
  ContentSeries: 'content_series',
  ContentTitle: 'content_title',

  // Operating System - Samsung only
  OperatingSystem: 'operating_system',

  // Freewheel or Unity only
  ORTBWins: 'ortb_wins',

  // VPAID Events
  VPAIDErrors: 'vpaid_errors',
  VPAIDStart: 'vpaid_start',
  VPAIDImpressions: 'vpaid_impressions',
  VPAIDQuartile1: 'vpaid_quartile1',
  VPAIDQuartile2: 'vpaid_quartile2',
  VPAIDQuartile3: 'vpaid_quartile3',
  VPAIDComplete: 'vpaid_complete',

  // IAS First and Third Party Group Bys
  MediaTypeID: 'media_type_id',
  // IAS First and Third Party Metrics
  TrackedAds: 'tracked_ads',
  ImpressionRendered: 'impression_rendered',
  IVTAds: 'ivt_ads',
  IVTRate: 'ivt_rate',
  MeasuredAds: 'measured_ads',
  MeasuredRate: 'measured_rate',
  ViewableImpression: 'viewable_impression',
  ViewabilityRate: 'viewability_rate',
  // IAS First Party Metrics (render rate already exist)
  EligibleAds: 'eligible_ads',
  AdBlockCount: 'ad_block_count',
  AdBlockRate: 'ad_block_rate',
  // IAS First Party Group Bys (LineItemID, CreativeID, CreativeName, AdvertiserName, OrderName already exist)
  AdvertiserID: 'advertiser_id',
  AdUnitID: 'ad_unit_id',
  AdSize: 'ad_size',
  OrderID: 'order_id',
  AdUnitPath: 'ad_unit_path',
  LineItemName: 'line_item_name',
  // IAS Third Party Group Bys
  PublicaChannelID: 'publica_channel_id',
  PublicaBidderID: 'publica_bidder_id',
  PlacementID: 'placement_id',
  // IAS Third Party Metrics
  GIVTAds: 'givt_ads',
  GIVTRate: 'givt_rate',
  SIVTAds: 'sivt_ads',
  SIVTRate: 'sivt_rate',
  URLFailRate: 'url_fail_rate',
  IVTFailRate: 'ivt_fail_rate',
  ValidVideoAds: 'valid_video_ads',
  Quartile1: 'quartile1',
  Quartile2: 'quartile2',
  Quartile3: 'quartile3',
  Quartile1Rate: 'quartile1_rate',
  Quartile2Rate: 'quartile2_rate',
  Quartile3Rate: 'quartile3_rate',
  OnscreenComplete: 'onscreen_complete',
  OnscreenCompletionRate: 'onscreen_completion_rate',
  PassedImps: 'passed_imps',
  PassRate: 'pass_rate',
  FailedImps: 'failed_imps',
  FailedAdRate: 'failed_ad_rate',
  ValidViewableVideoAds: 'valid_viewable_video_ads',
  ViewableQuartile1: 'viewable_quartile1',
  ViewableQuartile2: 'viewable_quartile2',
  ViewableQuartile3: 'viewable_quartile3',
  ViewableComplete: 'viewable_complete',
  ViewableQuartile1Rate: 'viewable_quartile1_rate',
  ViewableQuartile2Rate: 'viewable_quartile2_rate',
  ViewableQuartile3Rate: 'viewable_quartile3_rate',
  ViewableCompleteRate: 'viewable_complete_rate',
  UnmeasuredImps: 'unmeasured_imps',
  UnmeasuredImpsRate: 'unmeasured_imps_rate',
  NonViewableImpression: 'non_viewable_impression',
  NonViewableImpRate: 'non_viewable_imp_rate',
  AppliedContextualSegmentPartners: 'applied_contextual_segment_partners',

  // Creative Validation Report (VenueID already exist)
  MediaFile: 'media_file',

  GlobalTimeout: 'global_timeout',

  // EleaAI Scan All
  EleaAIBidErrors: 'elea_ai_bid_errors',
  EleaAIBidErrorsDifferentAdomain: 'elea_ai_bid_errors_different_adomain',
  EleaAIBidErrorsDifferentAdomainRate: 'elea_ai_bid_errors_different_adomain_rate',
  EleaAIBidLosts: 'elea_ai_bid_losts',
  EleaAIBidLostsDifferentAdomain: 'elea_ai_bid_losts_different_adomain',
  EleaAIBidLostsDifferentAdomainRate: 'elea_ai_bid_losts_different_adomain_rate',
  EleaAIBidResponses: 'elea_ai_bid_responses',
  EleaAIBidResponsesDifferentAdomain: 'elea_ai_bid_responses_different_adomain',
  EleaAIBidResponsesDifferentAdomainRate: 'elea_ai_bid_responses_different_adomain_rate',
  EleaAIBidWons: 'elea_ai_bid_wons',
  EleaAIBidWonsDifferentAdomain: 'elea_ai_bid_wons_different_adomain',
  EleaAIBidWonsDifferentAdomainRate: 'elea_ai_bid_wons_different_adomain_rate',
  EleaAIDetectedLanguage: 'elea_ai_detected_language',
  EleaAIEnrichedAdomain: 'elea_ai_enriched_adomain',
  EleaAIImpressions: 'elea_ai_impressions',
  EleaAIImpressionsDifferentAdomain: 'elea_ai_impressions_different_adomain',
  EleaAIImpressionsDifferentAdomainRate: 'elea_ai_impressions_different_adomain_rate',
  EleaAIMediafile: 'elea_ai_mediafile',
  EleaAIOriginalAdomain: 'elea_ai_original_adomain',
  EleaAIDetectedLanguageBlockedBids: 'elea_ai_detected_language_blocked_bids',
  // EleaAI Enrichment
  EleaAIBidWonRate: 'elea_ai_bid_won_rate',
  EleaAIImpressionRate: 'elea_ai_impression_rate',
  EleaAINumTotalAdomainsBidErrors: 'elea_ai_num_total_adomains_bid_errors',
  EleaAINumTotalAdomainsBidLosts: 'elea_ai_num_total_adomains_bid_losts',
  EleaAINumTotalAdomainsBidResponses: 'elea_ai_num_total_adomains_bid_responses',
  EleaAINumTotalAdomainsBidWons: 'elea_ai_num_total_adomains_bid_wons',
  EleaAINumTotalAdomainsImpressions: 'elea_ai_num_total_adomains_impressions',
  EleaAINumTotalDetectedLanguagesBidErrors: 'elea_ai_num_total_detected_languages_bid_errors',
  EleaAINumTotalDetectedLanguagesBidLosts: 'elea_ai_num_total_detected_languages_bid_losts',
  EleaAINumTotalDetectedLanguagesBidResponses: 'elea_ai_num_total_detected_languages_bid_responses',
  EleaAINumTotalDetectedLanguagesBidWons: 'elea_ai_num_total_detected_languages_bid_wons',
  EleaAINumTotalDetectedLanguagesImpressions: 'elea_ai_num_total_detected_languages_impressions',
  EleaAINumTotalMediafilesBidErrors: 'elea_ai_num_total_mediafiles_bid_errors',
  EleaAINumTotalMediafilesBidLosts: 'elea_ai_num_total_mediafiles_bid_losts',
  EleaAINumTotalMediafilesBidResponses: 'elea_ai_num_total_mediafiles_bid_responses',
  EleaAINumTotalMediafilesBidWons: 'elea_ai_num_total_mediafiles_bid_wons',
  EleaAINumTotalMediafilesImpressions: 'elea_ai_num_total_mediafiles_impressions',
  EleaAINumUniqueAdomainsBidErrors: 'elea_ai_num_unique_adomains_bid_errors',
  EleaAINumUniqueAdomainsBidLosts: 'elea_ai_num_unique_adomains_bid_losts',
  EleaAINumUniqueAdomainsBidResponses: 'elea_ai_num_unique_adomains_bid_responses',
  EleaAINumUniqueAdomainsBidWons: 'elea_ai_num_unique_adomains_bid_wons',
  EleaAINumUniqueAdomainsImpressions: 'elea_ai_num_unique_adomains_impressions',
  EleaAINumUniqueDetectedLanguagesBidErrors: 'elea_ai_num_unique_detected_languages_bid_errors',
  EleaAINumUniqueDetectedLanguagesBidLosts: 'elea_ai_num_unique_detected_languages_bid_losts',
  EleaAINumUniqueDetectedLanguagesBidResponses:
    'elea_ai_num_unique_detected_languages_bid_responses',
  EleaAINumUniqueDetectedLanguagesBidWons: 'elea_ai_num_unique_detected_languages_bid_wons',
  EleaAINumUniqueDetectedLanguagesImpressions: 'elea_ai_num_unique_detected_languages_impressions',
  EleaAINumUniqueMediafilesBidErrors: 'elea_ai_num_unique_mediafiles_bid_errors',
  EleaAINumUniqueMediafilesBidLosts: 'elea_ai_num_unique_mediafiles_bid_losts',
  EleaAINumUniqueMediafilesBidResponses: 'elea_ai_num_unique_mediafiles_bid_responses',
  EleaAINumUniqueMediafilesBidWons: 'elea_ai_num_unique_mediafiles_bid_wons',
  EleaAINumUniqueMediafilesImpressions: 'elea_ai_num_unique_mediafiles_impressions',
  // IAS Publisher Reports
  TotalImps: 'total_imps',
  IvtAds: 'ivt_ads',
  IvtRate: 'ivt_rate',
  // GroupM
  GroupmFullyInViewDisplayImps: 'groupm_fully_in_view_display_imps',
  GroupmFullyInViewVideoImps: 'groupm_fully_in_view_video_imps',
  TotalBillableImps: 'total_billable_imps',
  GroupmDisplayViewableRate: 'groupm_display_viewable_rate',
  GroupmVideoViewableRate: 'groupm_video_viewable_rate',
  // Invalid Traffic
  SittingDuckBotImps: 'sitting_duck_bot_imps',
  StandardBotImps: 'standard_bot_imps',
  VolunteerBotImps: 'volunteer_bot_imps',
  ProfileBotImps: 'profile_bot_imps',
  MaskedBotImps: 'masked_bot_imps',
  NomadicBotImps: 'nomadic_bot_imps',
  OtherBotImps: 'other_bot_imps',
  // Failure Risk Categories
  FailedAdultAds: 'failed_adult_ads',
  FailedAlcoholAds: 'failed_alcohol_ads',
  FailedHateSpeechAds: 'failed_hate_speech_ads',
  FailedIllegalDownloadsAds: 'failed_illegal_downloads_ads',
  FailedDrugsAds: 'failed_drugs_ads',
  FailedOffensiveLanguageAds: 'failed_offensive_language_ads',
  FailedViolenceAds: 'failed_content_violence_ads',
  // Ad Refresh
  RefreshImps: 'refresh_imps',
  RefreshThresholdEvents: 'refresh_threshold_events',
  RefreshedAdRate: 'refreshed_ad_rate',
  IASRefreshedAdRate: 'ias_refreshed_ad_rate',
  // Publicis Viewability
  PublicisFullyInViewDisplayImps: 'publicis_fully_in_view_display_imps',
  PublicisFullyInViewVideoImps: 'publicis_fully_in_view_video_imps',
  PublicisDisplayViewableRate: 'publicis_display_viewable_rate',
  PublicisVideoViewableRate: 'publicis_video_viewable_rate',
  // Blocked Reason
  BlockedContentImps: 'blocked_content_imps',
  BlockedSuitabilityImps: 'blocked_suitability_imps',
  BlockedKeywordImps: 'blocked_keyword_imps',
  BlockedCustomListImps: 'blocked_custom_list_imps',
  BlockedGeoImps: 'blocked_geo_imps',
  BlockedSuspiciousImps: 'blocked_suspicious_imps',
  BlockedSeeThroughImps: 'blocked_see_through_imps',
  BlockedLanguageImps: 'blocked_language_imps',
  // Exposure
  InViewAtPageOpenRate: 'in_view_at_page_open_rate',
  InViewOver5sRate: 'in_view_over_5s_rate',
  InViewOver15sRate: 'in_view_over_15s_rate',
  // Out of View Ads
  MrcOvrNetImps: 'mrc_ovr_net_imps',
  MrcOvrNepImps: 'mrc_ovr_nep_imps',
  MrcOvrNivImps: 'mrc_ovr_niv_imps',
  MrcOvrOofImps: 'mrc_ovr_oof_imps',
  MrcOvrNsImps: 'mrc_ovr_ns_imps',
  MrcOvrUnkImps: 'mrc_ovr_unk_imps',
  // Failure Reasons
  FailedContentImps: 'failed_content_imps',
  FailedSuitabilityImps: 'failed_suitability_imps',
  FailedKeywordImps: 'failed_keyword_imps',
  FailedCustomListImps: 'failed_custom_list_imps',
  FailedGeoImps: 'failed_geo_imps',
  FailedSuspiciousImps: 'failed_suspicious_imps',
  FailedSeeThroughImps: 'failed_see_through_imps',
  FailedLanguageImps: 'failed_language_imps',
  // Blocked Risk Categories
  BlockedContentAdultImps: 'blocked_content_adult_imps',
  BlockedContentAlcoholImps: 'blocked_content_alcohol_imps',
  BlockedContentHateImps: 'blocked_content_hate_imps',
  BlockedContentDownloadsImps: 'blocked_content_downloads_imps',
  BlockedContentDrugsImps: 'blocked_content_drugs_imps',
  BlockedContentOffensiveImps: 'blocked_content_offensive_imps',
  BlockedContentViolenceImps: 'blocked_content_violence_imps',
  // Video
  VideoInViewRate: 'video_in_view_rate',
  VideoOutOfViewRate: 'video_out_of_view_rate',
  VideoNeverStartedRate: 'video_never_started_rate',
  VideoCompletedRate: 'video_completed_rate',
  VideoCompletedInViewRate: 'video_completed_in_view_rate',
  VideoMutedRate: 'video_muted_rate',
  VideoFullScreenRate: 'video_full_screen_rate',
  VideoClickThroughRate: 'video_click_through_rate',
  // Invalid Traffic By Type
  FraudGivtImps: 'fraud_givt_imps',
  FraudNhtImps: 'fraud_nht_imps',
  FraudHaImps: 'fraud_ha_imps',
  FraudDsImps: 'fraud_ds_imps',
  FraudIbImps: 'fraud_ib_imps',
  FraudLspImps: 'fraud_lsp_imps',
  SophisticatedInvalidTrafficAds: 'sophisticated_invalid_traffic_ads',
  ReducedValueInventoryAds: 'reduced_value_inventory_ads',
  GeneralInvalidTrafficRate: 'general_invalid_traffic_rate',
  SophisticatedInvalidTrafficRate: 'sophisticated_invalid_traffic_rate',
  ReducedValueInventoryRate: 'reduced_value_inventory_rate',

  // TTD Report
  CurrencyType: 'currency_type',
  Timezone: 'timezone',
  MarketType: 'market_type',
  MediaType: 'media_type',
  PublisherId: 'publisher_id',
  MediaCost: 'media_cost',

  // Creative Review
  ReviewStatus: 'review_status',
  NumberOfBlocks: 'number_of_blocks',
  Username: 'username',
  Timestamp: 'timestamp',

  // Prebid, postbid and bid rejections errors
  PrebidErrors: 'prebid_errors',
  PostbidErrors: 'postbid_errors',
  BidRejections: 'bid_rejections',
  // Pod Hourly Report
  AdvertiserRuleCpmRev: 'advertiser_rule_cpm_rev',
  AdvertiserRuleCpmsRev: 'advertiser_rule_cpms_rev',
  AdvertiserRulePodLift: 'advertiser_rule_pod_lift',
  CpmRev: 'cpm_rev',
  CpmsRev: 'cpms_rev',
  EventPodCompare: 'event_pod_compare',
  EventPodCompareWithAdvertiserRules: 'event_pod_compare_with_advertiser_rules',
  EventPodCompareWithoutAdvertiserRules: 'event_pod_compare_without_advertiser_rules',
  IsPhiloFallbackAuction: 'is_philo_fallback_auction',
  NoBidSaverRev: 'no_bid_saver_rev',
  NumberOfWinners: 'number_of_winners',
  PodComparePodDuration: 'pod_compare_pod_duration',
  PodCompareRevenue: 'pod_compare_revenue',
  PodCompareType: 'pod_compare_type',
  PodDurationBackfilled: 'pod_duration_backfilled',
  PodDurationOwnedByNonSOVBidder: 'pod_duration_owned_by_non_sov_bidder',
  PodDurationOwnedBySOVBidder: 'pod_duration_owned_by_sov_bidder',
  PodLevelSOVType: 'pod_level_sov_type',
  TTDResponses: 'ttd_responses',
  TTDSpend: 'ttd_spend',
  TTDWins: 'ttd_wins',
  TTDLosses: 'ttd_losses',
  TTDLossesDoNotAir: 'ttd_losses_do_not_air',
  TTDLossesCompSepSelf: 'ttd_losses_comp_sep_self',
  TTDLossesCompSepOther: 'ttd_losses_comp_sep_other',
  TTDLossesPriceOrTier: 'ttd_losses_price_or_tier',
  TTDLossesOther: 'ttd_losses_other',
  HasAdvertiserRuleResponses: 'has_advertiser_rule_responses',
  // Third Party Vast Tracker Domain
  VastTrackerEndpointDomain: 'vast_tracker_endpoint_domain',
  TTDLossesPrice: 'ttd_losses_price',
  TTDLossesTier: 'ttd_losses_tier',
  // Bid Floor Settings Report Only
  BidFloor: 'bid_floor',
  // Auction Report and Bidder Report for Samsung Pubs only
  HasGooglePalSDKGivnParam: 'has_google_pal_sdk_givn_param',
  BuyerID: 'buyer_id',

  // Smart Floors
  ControlBidRequests: 'control_bid_requests',
  OptimizedBidRequests: 'optimized_bid_requests',
  LowBidRequests: 'low_bid_requests',
  HighBidRequests: 'high_bid_requests',
  ControlBidResponses: 'control_bid_responses',
  OptimizedBidResponses: 'optimized_bid_responses',
  LowBidResponses: 'low_bid_responses',
  HighBidResponses: 'high_bid_responses',
  ControlBidWons: 'control_bid_wons',
  OptimizedBidWons: 'optimized_bid_wons',
  LowBidWons: 'low_bid_wons',
  HighBidWons: 'high_bid_wons',
  ControlAvgWinningBid: 'control_avg_winning_bid',
  OptimizedAvgWinningBid: 'optimized_avg_winning_bid',
  LowAvgWinningBid: 'low_avg_winning_bid',
  HighAvgWinningBid: 'high_avg_winning_bid',
  ControlBidFloor: 'control_bid_floor',
  OptimizedBidFloor: 'optimized_bid_floor',
  LowBidFloor: 'low_bid_floor',
  HighBidFloor: 'high_bid_floor',
  // Traffic Shaping
  TrafficShapingOptimizedBidRequests: 'traffic_shaping_optimized_bid_requests',
  TrafficShapingControlBidRequests: 'traffic_shaping_control_bid_requests',
  TrafficShapingOptimizedBidResponses: 'traffic_shaping_optimized_bid_responses',
  TrafficShapingControlBidResponses: 'traffic_shaping_control_bid_responses',
  TrafficShapingOptimizedBidResponse_rate: 'traffic_shaping_optimized_bid_response_rate',
  TrafficShapingControlBidResponse_rate: 'traffic_shaping_control_bid_response_rate',
  TrafficShapingOptimizedBidImpressions: 'traffic_shaping_optimized_bid_impressions',
  TrafficShapingControlBidImpressions: 'traffic_shaping_control_bid_impressions',
  TrafficShapingOptimizedBidRevenue: 'traffic_shaping_optimized_bid_revenue',
  TrafficShapingControlBidRevenue: 'traffic_shaping_control_bid_revenue',
  TrafficShapingBidRequestDifference: 'traffic_shaping_bid_request_difference',
  TrafficShapingBidResponseDifference: 'traffic_shaping_bid_response_difference',
  TrafficShapingBidImpressionsDifference: 'traffic_shaping_bid_impressions_difference',
  TrafficShapingBidResponseRateDifference: 'traffic_shaping_bid_response_rate_difference',
  TrafficShapingRevenueDifference: 'traffic_shaping_revenue_difference',
  RequestsOverRevenue: 'requests_over_revenue',
};

export interface ISQLOption {
  key: string;
  text: string;
}

export function toSQLOption(col: IColumn): ISQLOption {
  return {
    key: keyToSQLField[col.name],
    text: col.header,
  };
}

function generateStringFromArray(arr: string[], includeQuotes: boolean): string {
  if (!arr) {
    return '';
  }
  const resultArr = includeQuotes ? arr.map((v) => `\'${v}\'`) : arr;
  return resultArr.join(',');
}

function generateReportFilterConfigParams(ReportFilterConfig: IReportFilterConfig) {
  return {
    ...ReportFilterConfig,
    CountryGroupIDs: (ReportFilterConfig.CountryGroupIDs || []).join(','),
    ChannelLabelIDs: (ReportFilterConfig.ChannelLabelIDs || []).join(','),
    BidderLabelIDs: (ReportFilterConfig.BidderLabelIDs || []).join(','),
    OrderIDs: (ReportFilterConfig.OrderIDs || []).join(','),
    AdvertiserIDs: (ReportFilterConfig.AdvertiserIDs || []).join(','),
  };
}

export function generateReportParams(
  report: IReport,
  cfg: {
    graph_id?: string;
    publisher_id: string;
    start_date: string;
    end_date: string;
    timezone: string;
    currency: number;
    exchangerate: number;
    includeQuotes: boolean;
    report_type: string;
    report_duration_days?: number;
    report_type_string?: string;
    ias_body?: IIASFirstAndThirdPartyRequestBody;
    an_id?: number;
  },
) {
  const groupbyoptions = generateStringFromArray(report.GroupByOptions, cfg.includeQuotes);
  const metrics = generateStringFromArray(report.Metrics, cfg.includeQuotes);
  const deviceEnvNames = report.DeviceEnvs
    ? report.DeviceEnvs.map((item) => deviceTargetingMap[item])
    : [];
  // when adding new filters here, leave an empty or string in case it isn't defined on the report
  // some reports like ssp report don't have all these fields so we can default them to empty
  return {
    ...cfg,
    adomains: report.Adomains,
    countries: generateStringFromArray(report.Countries, cfg.includeQuotes),
    deal_ids: report.DealIDs,
    deal_names: generateStringFromArray(report.DealNames, cfg.includeQuotes),
    deviceenvs: generateStringFromArray(deviceEnvNames, cfg.includeQuotes),
    devicetypes: generateStringFromArray(report.DeviceTypes, cfg.includeQuotes),
    content_channel_filter: generateStringFromArray(report.ContentChannelFilter, false),
    content_network_filter: generateStringFromArray(report.ContentNetworkFilter, false),
    orig_content_channel_filter: generateStringFromArray(report.OrigContentChannelFilter, false),
    orig_content_network_filter: generateStringFromArray(report.OrigContentNetworkFilter, false),
    device_subtypes: generateStringFromArray(report.DeviceSubtypes, cfg.includeQuotes),
    channel_group_ids: (report.ChannelGroupIDS || []).join(','),
    site_ids: (report.SiteIDs || []).join(','),
    siteids: (report.SiteIDs || []).join(','),
    bidder_ids: (report.BidderIDs || []).filter((id: string) => id !== '').join(','),
    bidder_group_ids: (report.BidderGroupsIDs || []).join(','),
    campaign_ids: (report.CampaignIDs || []).join(','),
    segment_ids: (report.SegmentIDs || []).join(','),
    inventory_route_ids: (report.InventoryRouteIDs || []).join(','),
    destination_ids: (report.DestinationIDs || []).join(','),
    biddercodes: generateStringFromArray(report.BidderCodes, cfg.includeQuotes),
    metrics,
    groupbyoptions,
    reportgrouptype: getReportGroupType(groupbyoptions),
    report_type: report.ReportType,
    grouping: generateGroupingID(report.GroupByOptions || []),
    report_limit: report.ReportLimit || '',
    report_order_by: report.ReportOrderBy || '',
    dmas: report.DMAs || '',
    states: (report.States || []).join(','),
    network_ids: (report.NetworkIDs || []).join(','),
    stream_types: (report.StreamTypes || []).join(','),
    report_id: report.ID,
    report_name: report.Name,
    subject: report.Subject,
    recipients: report.Recipients,
    venue_ids: report.VenueIDs,
    is_site_ids_included: report.IsSiteIDsIncluded || 2,
    IsPartialAdBreak: report.ReportFilterConfig?.IsPartialAdBreak || '',
    is_bidder_ids_included: report.IsBidderIDsIncluded || 2,
    order_aliases: (report.OrderAliases || []).join(','),
    line_item_aliases: (report.LineItemAliases || []).join(','),
    should_upload_to_external_s3: report.ShouldUploadToExternalS3,
    bidder_tier_filter: (report.BidderTierFilter || []).join(','),

    // --- USE THIS FOR FUTURE FILTERS --- //
    ReportFilterConfig: generateReportFilterConfigParams(
      report.ReportFilterConfig || { ...defaultReportFilterConfig },
    ),
  };
}

// Generate the correct grouping identifier (hour=0, ...)
// default is DAY (1)
function generateGroupingID(groupByOptions: string[]) {
  if (groupByOptions.includes('hour')) {
    return 0;
  }
  if (groupByOptions.includes('weeks')) {
    return 2;
  }
  if (groupByOptions.includes('month')) {
    return 3;
  }
  if (groupByOptions.includes('week_of_year')) {
    return 4;
  }
  return 1;
}

export function generateEmailParams(
  report: IReport,
  cfg: {
    graph_id?: string;
    publisher_id: string;
    report_name: string;
    start_date: string;
    end_date: string;
    timezone: string;
    currencydisplayed: number;
    priority?: string;
    email?: string;
  },
) {
  const groupbyoptions = generateStringFromArray(report.GroupByOptions, false);
  const deviceEnvNames = report.DeviceEnvs
    ? report.DeviceEnvs.map((item) => deviceTargetingMap[item])
    : [];
  return {
    ...cfg,
    adomains: report.Adomains,
    countries: report.Countries.join(','),
    states: report.States.join(','),
    deal_ids: report.DealIDs,
    deal_names: report.DealNames.join(','),
    deviceenvs: deviceEnvNames.join(','),
    devicetypes: report.DeviceTypes.join(','),
    channel_group_ids: (report.ChannelGroupIDS || []).join(','),
    site_ids: report.SiteIDs.join(','),
    siteids: report.SiteIDs.join(','),
    bidder_ids: report.BidderIDs.join(','),
    bidder_group_ids: (report.BidderGroupsIDs || []).join(','),
    segment_ids: report.SegmentIDs.join(','),
    campaign_ids: report.CampaignIDs.join(','),
    network_ids: report.NetworkIDs.join(','),
    biddercodes: report.BidderCodes.join(','),
    metrics: generateStringFromArray(report.Metrics, false),
    groupbyoptions,
    report_limit: report.ReportLimit || '0',
    report_order_by: report.ReportOrderBy,
    reportgrouptype: getReportGroupType(groupbyoptions),
    reporttype: report.ReportType,
    period: report.Period,
    recipients: cfg.email ? cfg.email : report.Recipients.join(','),
    subject: report.Subject,
    stream_types: report.StreamTypes.join(','),
    venue_ids: report.VenueIDs,
    is_site_ids_included: report.IsSiteIDsIncluded || 2,
    is_bidder_ids_included: report.IsBidderIDsIncluded || 2,
    order_aliases: (report.OrderAliases || []).join(','),
    line_item_aliases: (report.LineItemAliases || []).join(','),
    should_upload_to_external_s3: report.ShouldUploadToExternalS3,
    bidder_tier_filter: generateStringFromArray(report.BidderTierFilter, false),
    content_channel_filter: generateStringFromArray(report.ContentChannelFilter, false),
    content_network_filter: generateStringFromArray(report.ContentNetworkFilter, false),
    orig_content_channel_filter: generateStringFromArray(report.OrigContentChannelFilter, false),
    orig_content_network_filter: generateStringFromArray(report.OrigContentNetworkFilter, false),
    // --- USE THIS FOR FUTURE FILTERS --- //
    ReportFilterConfig: generateReportFilterConfigParams(report.ReportFilterConfig),
    report_id: report.ID || '0',
  };
}

export const parseBidErrorCode = (val: string) => {
  return BID_ERROR_CODE_MAP[val] || BID_ERROR_CODE_MAP[''];
};

export const parseEndpointErrorCode = (val: number) => {
  return ENDPOINT_ERROR_CODE_MAP[String(val)] || ENDPOINT_ERROR_CODE_MAP[''];
};

export const parseInventoryRouteErrorCode = (val: number) => {
  if (val === 0) return ENDPOINT_ERROR_CODE_MAP[''];
  return ENDPOINT_ERROR_CODE_MAP[String(val)] || ENDPOINT_ERROR_CODE_MAP[''];
};

const destinationTypeToLabel: Record<DestinationType, string> = {
  [DestinationTypeSite]: 'Channel',
  [DestinationTypeVastURL]: 'VAST',
  [DestinationTypeRoute]: 'Route',
  [DestinationTypeUnknown]: 'Unknown',
};

export const parseDestinationType = (val: number) =>
  destinationTypeToLabel[val] || destinationTypeToLabel[DestinationTypeUnknown];

export const parseInventoryRouteCondition = (val: number) => {
  switch (val) {
    case RoutingConditionPercentageRequests:
      return 'Percentage Requests';
    case RoutingConditionTargetingParams:
      return 'Targeting Params';
    default:
      break;
  }
  return 'Unknown';
};

export const parseVastErrorCode = (val: string) => {
  return VAST_ERROR_CODE_MAP[val] || VAST_ERROR_CODE_MAP[''];
};

export function parseNoFillCode(val: string) {
  return CAMPAIGN_NO_FILL_ERROR_CODE_MAP[val] || CAMPAIGN_NO_FILL_ERROR_CODE_MAP[''];
}

export function parseBidWonRejectReason(val: string) {
  return BID_WON_REJECT_REASON_MAP[val] || BID_WON_REJECT_REASON_MAP[''];
}

export function parseTargetingDevice(val: string) {
  return TARGETING_DEVICE_MAP[val] || '';
}

export function parsePhiloFallback(val: string) {
  switch (val) {
    case '1':
      return 'SOV Primary';
    case '2':
      return 'SOV Fallback';
    case '3':
      return 'Philo Primary';
    case '4':
      return 'Philo Backfill';
    default:
      break;
  }
  return 'SOV Unknown';
}

export function parseCreativeReviewStatus(val: string) {
  return CREATIVE_REVIEW_STATUS_MAP[val] || CREATIVE_REVIEW_STATUS_MAP[''];
}

export function parseGppConsentStatus(val: number | string): string {
  const valInt = typeof val === 'string' ? parseInt(val, 10) : val;
  if (typeof valInt !== 'number' || isNaN(valInt)) {
    return 'N/A';
  }
  switch (valInt) {
    case 0:
      return 'Empty String';
    case 1:
      return 'INVALID';
    case 2:
      return 'OPT OUT';
    case 3:
      return 'OPT IN';
    default:
      return 'N/A';
  }
}

export function mapBidderType(data: IAnalytics[]) {
  return data.map((item: IAnalytics) => {
    item.BidderType = item.BidderType === '' ? '(deprecated)' : item.BidderType;

    return item;
  });
}

export const genAuctionRequestersList = () => {
  const auctionRequesters = [
    { name: 'StandardEndpoint', label: 'VAST' },
    { name: 'Freewheel', label: 'Freewheel' },
    { name: 'AttWarner', label: 'AttWarner' },
    { name: 'VMAP', label: 'VMAP' },
  ];

  const auctionRequestersList: IMenuList = {};
  Object.values(auctionRequesters).forEach((requester) => {
    auctionRequestersList[requester.name] = {
      title: requester.label,
      selected: false,
      ID: requester.name,
    };
  });

  return auctionRequestersList;
};

const clusters = [
  { name: 'EKS_23', label: 'eks-23' },
  { name: 'EKS_23_PBS_CONTROL', label: 'eks-23-pbs-control' },
  { name: 'EKS_23_PBS_EXPERIMENTAL', label: 'eks-23-pbs-experimental' },
  { name: 'EKS_23_VMAP_PBS_CONTROL', label: 'eks-23-vmap-pbs-control' },
  { name: 'EKS_23_VMAP_PBS_EXPERIMENTAL', label: 'eks-23-vmap-pbs-experimental' },
  { name: 'EKS_23_PBS_WINGMAN_CONTROL', label: 'eks-23-pbs-wingman-control' },
  { name: 'EKS_23_PBS_WINGMAN_EXPERIMENTAL', label: 'eks-23-pbs-wingman-experimental' },
  { name: 'EKS_23_PBS_LOAD_TEST_CONTROL', label: 'eks-23-pbs-load-test-control' },
  { name: 'EKS_23_PBS_LOAD_TEST_EXPERIMENTAL', label: 'eks-23-pbs-load-test-experimental' },
  { name: 'EKS_24', label: 'eks-24' },
  { name: 'EKS_25', label: 'eks-25' },
  { name: 'EKS_26', label: 'eks-26' },
  { name: 'EKS_16', label: 'eks-16' },
  { name: 'EKS_17', label: 'eks-17' },
  { name: 'EKS_33', label: 'eks-33' },
  { name: 'EKS_34', label: 'eks-34' },
  { name: 'EKS_50', label: 'eks-50' },
  { name: 'EKS_51', label: 'eks-51' },
  { name: 'EKS_52', label: 'eks-52' },
];

export const genClusters = () => {
  const clusterList: IMenuList = {};
  Object.values(clusters).forEach((cluster) => {
    clusterList[cluster.name] = {
      title: cluster.label,
      selected: false,
      ID: cluster.name,
    };
  });

  return clusterList;
};

const regions = [
  { name: 'US_EAST_1', label: 'US East 1' },
  { name: 'EU_WEST_1', label: 'EU West 1' },
  { name: 'AP_SOUTHEAST_2', label: 'AP Southeast 2' },
  { name: 'AP_NORTHEAST_1', label: 'AP Northeast 1' },
];

export const genRegions = () => {
  const regionList: IMenuList = {};
  Object.values(regions).forEach((region) => {
    regionList[region.name] = {
      title: region.label,
      selected: false,
      ID: region.name,
    };
  });

  return regionList;
};

const domains = [
  { name: 'pbs.getpublica.com', label: 'pbs.getpublica.com' },
  { name: 'pbs-eu.getpublica.com', label: 'pbs-eu.getpublica.com' },
  { name: 'pbs-au.getpublica.com', label: 'pbs-au.getpublica.com' },
  { name: 'pbs-jp.getpublica.com', label: 'pbs-jp.getpublica.com' },
  { name: 'pbs-us-r0.getpublica.com', label: 'pbs-us-r0.getpublica.com' },
  { name: 'pbs-us-r1.getpublica.com', label: 'pbs-us-r1.getpublica.com' },
];

export const genDomains = (): IMenuList => {
  const domainList: IMenuList = {};
  Object.values(domains).forEach((domain) => {
    domainList[domain.name] = {
      title: domain.label,
      selected: false,
      ID: domain.name,
    };
  });

  return domainList;
};

const predictiveSmoothingCohorts = [
  { name: '0', label: 'Smoothing Noop Group' },
  { name: '1', label: 'Smoothing Experimental Group' },
  { name: '2', label: 'Smoothing Control Group' },
];

export const genPredictiveSmoothingCohortsList = (): IMenuList => {
  const predictiveSmoothingList: IMenuList = {};
  Object.values(predictiveSmoothingCohorts).forEach((ps) => {
    predictiveSmoothingList[ps.name] = {
      title: ps.label,
      selected: false,
      ID: ps.name,
    };
  });

  return predictiveSmoothingList;
};
